import { useParams } from "react-router-dom";
import React from "react";
import { useFetch } from "@/hooks/useFetch";
import { fetchTicketDetails } from "./api";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";
import ErrorState from "@/components/states/error";
import EmptyState from "@/components/feedback/empty-state";
import TicketHeader from "./components/header";
import TicketDescription from "./components/description";
import TicketAttachments from "./components/attachments";
import TicketActivity from "./components/activity";
import TicketDetailsSidebar from "./components/details-sidebar";
import Loader from "@/components/loader";

export default function TicketDetail() {
  const { ticketId } = useParams();

  const user = useSelector(selectUser);

  const { data, isLoading, error } = useFetch({
    queryKey: ["ticket", ticketId],
    queryFn: () => fetchTicketDetails(ticketId),
  });

  const canReply = React.useMemo(() => {
    if (!user || !data) return false;

    const assignedUserId =
      typeof data.assigned_to === "object"
        ? data.assigned_to?.id
        : data.assigned_to;

    const createdUserId =
      typeof data.user_created === "object"
        ? data.user_created?.id
        : data.user_created;



    return (user.id === assignedUserId || user.id === createdUserId) && data.status !== "closed";
  }, [user, data]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorState
        title="Error loading ticket details"
        message={error?.message}
      />
    );
  }

  if (!data) {
    return <EmptyState message="No ticket found" />;
  }

  return (
    <div>
      <TicketHeader
        title={data.title}
        status={data.status}
        userCreated={data.user_created?.id}
        ticketId={ticketId}
        assignedTo={data.assigned_to?.id}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <TicketDescription description={data.description} />

            <TicketAttachments attachments={data.attachments} />

            <TicketActivity
              chats={data.chats}
              canReply={canReply}
              ticketId={ticketId}
            />
          </div>

          <div className="space-y-6">
            <TicketDetailsSidebar
              assigned_to={data.assigned_to}
              priority={data.priority}
              type={data.type}
              due_date={data.due_date}
              date_created={data.date_created}
              user_created={data.user_created}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
